<script setup>
import { computed, ref } from 'vue';

import Icon from 'next/icon/Icon.vue';
import FileIcon from 'next/icon/FileIcon.vue';
import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';

const props = defineProps({
  // The comment carrying the files: the gallery names its author and date.
  activity: { type: Object, required: true },
});

const openedImage = ref(null);

// The gallery reads a message attachment: a unix `created_at` and a `sender`.
const images = computed(() =>
  props.activity.attachments
    .filter(attachment => attachment.file_type === 'image')
    .map(attachment => ({
      ...attachment,
      created_at: Math.floor(Date.parse(props.activity.created_at) / 1000),
      sender: props.activity.actor,
    }))
);

const files = computed(() =>
  props.activity.attachments.filter(
    attachment => attachment.file_type !== 'image'
  )
);
</script>

<template>
  <div class="flex flex-col gap-2">
    <div v-if="images.length" class="flex flex-wrap gap-2">
      <button
        v-for="image in images"
        :key="image.id"
        type="button"
        class="h-24 overflow-hidden rounded-lg max-w-60 outline outline-1 outline-n-weak hover:outline-n-strong"
        data-testid="card-comment-image"
        @click="openedImage = image"
      >
        <img
          :src="image.thumb_url"
          :alt="image.file_name"
          class="object-cover h-full"
        />
      </button>
    </div>
    <a
      v-for="file in files"
      :key="file.id"
      :href="file.data_url"
      target="_blank"
      rel="noopener noreferrer"
      class="flex items-center gap-2 px-3 py-2 text-sm rounded-lg w-fit max-w-full bg-n-alpha-1 text-n-slate-12 hover:bg-n-alpha-2"
      data-testid="card-comment-file"
    >
      <FileIcon
        :file-type="file.extension || ''"
        class="flex-shrink-0 size-4"
      />
      <span class="truncate">{{ file.file_name }}</span>
      <Icon
        icon="i-lucide-download"
        class="flex-shrink-0 size-4 text-n-slate-10"
      />
    </a>
    <GalleryView
      v-if="openedImage"
      show
      :attachment="openedImage"
      :all-attachments="images"
      @close="openedImage = null"
    />
  </div>
</template>
